import { useNavigate, useParams } from 'react-router-dom';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { useRef } from 'react';

export function getLanguageFromPath(pathname?: string): 'en' | 'ar' {
  const path = pathname || window.location.pathname;
  if (path.startsWith('/ar-eg/')) return 'ar';
  return 'en';
}

export const useCityNavigation = () => {
  const location = window.location;
  const language = getLanguageFromPath(location.pathname);
  const navigate = useNavigate();
  const { citySlug } = useParams();
  const { selectedCity } = useDeliveryLocation();
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getCityPrefix = () => {
    const currentCitySlug = citySlug || selectedCity?.slug;
    if (!currentCitySlug) return '';
    const langPrefix = language === 'ar' ? 'ar-eg' : 'en-eg';
    return `/${langPrefix}/${currentCitySlug}`;
  };

  const navigateWithCity = (path: string) => {
    // Clear any existing timeout to prevent rapid navigation
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    // Add a small delay to prevent rapid navigation calls
    navigationTimeoutRef.current = setTimeout(() => {
      const cityPrefix = getCityPrefix();
      // If path already starts with /en-eg/:citySlug or /ar-eg/:citySlug, don't prepend again
      if (/^\/(en-eg|ar-eg)\/[^/]+/.test(path)) {
        navigate(path);
      } else {
        const fullPath = cityPrefix ? `${cityPrefix}${path}` : path;
        navigate(fullPath);
      }
    }, 50);
  };

  const buildCityPath = (path: string) => {
    const cityPrefix = getCityPrefix();
    return cityPrefix ? `${cityPrefix}${path}` : path;
  };

  return {
    navigateWithCity,
    buildCityPath,
    getCityPrefix,
    getLanguageFromPath,
    currentCitySlug: citySlug || selectedCity?.slug
  };
};